import { Box, Flex } from '@withjoy/joykit';
import React, { useEffect, useState, type CSSProperties } from 'react';
import type { QRCodeLayerData } from './Layer.types';
import { textLayerDataToCSS } from './TextLayer';
import { useFont } from '../../../hooks/useFont';
import { useCreateQRCodeMutation } from '../CardCustomizer.graphql.generated';
import { StationeryDraftFormat } from '@graphql/generated-base';
import { layerLayoutToCss } from '@apps/card/utils';
import { resolveCssVarRef } from '@apps/card/utils/colorway';
import { cdn } from '@shared/utils/cdnAssets/cdn';
import { TEMPLATE_PLACEHOLDER_LINK } from '../CardCustomizer.constants';
import { useIsWithinRecipientView, useRecipientViewLinkClick } from './IsWithinRecipientViewContext';
import { getLayerLinkHref } from './Layer.utils';

const PLACEHOLDER_QR_IMAGE = cdn('joy-web/cards/customizer/placeholderQR.png');

export const QRCodeLayer = (props: {
  layer: QRCodeLayerData;
  canMove?: boolean;
  format?: StationeryDraftFormat;
  /**
   * Render the placeholder QR without generating a real one. Shopping surfaces
   * (gallery, PDP) set this: nobody can act on a QR for a card they haven't
   * bought, so the generated image is decoration not worth a request per tile
   * and per colorway swatch.
   */
  forcePlaceholder?: boolean;
  /** The page's colorway custom properties; resolves a `var(--N)` fill before it is sent to `createQRCode`. */
  cssVarStyle?: CSSProperties;
}) => {
  const { layer, canMove, format, forcePlaceholder, cssVarStyle } = props;
  const { link, password, fill: rawFill, font, text, hideImage } = layer.qrcodeData;
  const fill = resolveCssVarRef(rawFill, cssVarStyle);

  const isPlaceholderLink = forcePlaceholder || !link || link === TEMPLATE_PLACEHOLDER_LINK;
  // The whole layer (code, label and password) is one tap target in the recipient view.
  const isLink = useIsWithinRecipientView() && !isPlaceholderLink;
  const onLinkClick = useRecipientViewLinkClick();

  const [qrCodeImage, setQrCodeImage] = useState<string | undefined>(undefined);
  const [createQRCode] = useCreateQRCodeMutation();
  const hideQRCodeImage = format === StationeryDraftFormat.digital && hideImage;
  useEffect(() => {
    // Switching colorways changes `fill` while an earlier request may still be in flight; drop its response.
    let isStale = false;
    void (async () => {
      if (!hideQRCodeImage && !isPlaceholderLink) {
        try {
          const url = new URL(link);

          if (url) {
            // Print attribution regardless of format. A scan of a digital card's QR (e.g. shown on a
            // laptop, scanned with a phone) is credited as paper — rare enough to accept. Taps on a
            // digital card get ecard UTMs via getLayerLinkHref instead.
            url.searchParams.set('utm_source', 'qr');
            url.searchParams.set('utm_medium', 'paper');
            url.searchParams.set('utm_campaign', 'stationery_qr');
            url.searchParams.set('src', 'print');

            const { data } = await createQRCode({ variables: { data: { link: url.toString(), fillColor: fill } } });

            if (data && !isStale) {
              setQrCodeImage(data.createQRCode);
            }
          }
        } catch {
          // Clear rather than keep the previous QR, which would encode a stale link or colorway.
          if (!isStale) setQrCodeImage(undefined);
        }
      }
    })();
    return () => {
      isStale = true;
    };
  }, [createQRCode, link, hideQRCodeImage, isPlaceholderLink, fill]);

  useFont(`${font.family}:${font.weight}${font.style ?? ''}`);

  const alignItems = (() => {
    if (text.alignHorizontal === 'left') return 'flex-start';
    if (text.alignHorizontal === 'right') return 'flex-end';
    return 'center';
  })();

  // Display text below the QR code: strip UTMs so the printed card shows a clean, readable URL.
  // The QR code image itself still encodes the UTM-tagged URL (see `createQRCode` above) so the
  // scanner opens the attributed URL — only the human-readable label is normalized.
  const formattedLink = (() => {
    try {
      const url = new URL(link);
      (['utm_source', 'utm_medium', 'utm_campaign', 'utm_source_secondary', 'utm_content'] as const).forEach(key => url.searchParams.delete(key));
      return url.toString().replace(/^https?:\/\//, '');
    } catch {
      return link.replace(/^https?:\/\//, '');
    }
  })();
  const shouldHighlightLink = format === StationeryDraftFormat.digital;

  const showPlaceholderQR = !hideQRCodeImage && isPlaceholderLink;
  const showGeneratedQR = !hideQRCodeImage && !isPlaceholderLink && !!qrCodeImage;

  return (
    <Flex
      className="introable"
      cursor={canMove ? 'move' : isLink ? 'pointer' : 'default'}
      {...(isLink ? { as: 'a', href: getLayerLinkHref(link), target: '_blank', rel: 'noopener noreferrer', onClick: onLinkClick } : {})}
      userSelect="none"
      alignItems={alignItems}
      flexDirection="column"
      gap="5px"
      style={{
        ...textLayerDataToCSS(layer.qrcodeData),
        ...layerLayoutToCss(layer.layout),
        height: 'unset',
        wordBreak: 'break-word',
      }}
    >
      {showPlaceholderQR ? (
        // placeholder QR code image
        <Box
          draggable={false}
          width={72}
          height={72}
          style={{
            maskImage: `url(${PLACEHOLDER_QR_IMAGE})`,
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            // CSS resolves the raw `var(--N)` itself, including colorway vars inherited from an ancestor.
            backgroundColor: rawFill,
          }}
        />
      ) : showGeneratedQR ? (
        // generated QR code image
        <Box
          as="img"
          src={qrCodeImage}
          alt=""
          draggable={false}
          width={72}
          height={72}
        />
      ) : (
        // blank QR code image
        <Box
          draggable={false}
          width={72}
          height={72}
        />
      )}
      <Box>
        {shouldHighlightLink ? (
          <Box
            fontWeight="bold"
            style={{ textDecoration: 'underline 0.5px #D9D9D9', textUnderlineOffset: '4px', display: 'inline' }}
          >
            {formattedLink}
          </Box>
        ) : (
          formattedLink
        )}
        {password ? `\nPassword: ${password}` : ''}
      </Box>
    </Flex>
  );
};
